import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useLunaStore from '../store/useLunaStore';
import useLunaFlow from '../hooks/useLunaFlow'; // [New]
import api from '../services/api';
import Header from '../components/layout/Header';
import TermsModal from '../components/modals/TermsModal';

function buildFaClass(icon) {
    if (!icon) return 'fa-solid fa-circle-question';
    if (/^(fa-solid|fa-regular|fa-brands|fas|far|fab)\s/.test(icon)) return icon;
    return `fa-solid ${icon.startsWith('fa-') ? icon : `fa-${icon}`}`;
}

function SafeFaIcon({ icon, className }) {
    const ref = useRef(null);
    const [useFallback, setUseFallback] = useState(false);

    useEffect(() => {
        if (!ref.current) return;
        const check = () => {
            const content = window.getComputedStyle(ref.current, '::before').getPropertyValue('content');
            if (!content || content === 'none' || content === '""' || content === "''") {
                setUseFallback(true);
            }
        };
        document.fonts?.ready ? document.fonts.ready.then(check) : setTimeout(check, 500);
    }, [icon]);

    const cls = useFallback ? 'fa-solid fa-circle-question' : buildFaClass(icon);
    return <i ref={ref} className={`${cls}${className ? ` ${className}` : ''}`} />;
}

export default function RoadmapPage() {
    const navigate = useNavigate();
    const { setRoadmapMode, username, isSoundOn, setIsSoundOn, resetAll, newlyUnlockedTopic, setNewlyUnlockedTopic } = useLunaStore();
    const { saveCurrentState } = useLunaFlow(); // [New] 상태 저장용

    // State
    const [view, setView] = useState('loading'); // 'create', 'roadmap', 'loading'
    const [roadmap, setRoadmap] = useState(null);
    const [goal, setGoal] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showTerms, setShowTerms] = useState(false);

    // Load Roadmap on mount
    useEffect(() => {
        checkAuthAndLoad();
    }, []);

    const checkAuthAndLoad = async () => {
        try {
            const auth = await api.checkAuth();
            if (!auth.authenticated) {
                // navigate('/login'); // Optional: Redirect
            }

            const res = await api.getRoadmap();
            if (res.has_roadmap && res.roadmap) {
                setRoadmap(res.roadmap);
                updateProgress(res.roadmap);
                setView('roadmap');
            } else {
                setView('create');
            }
        } catch (error) {
            console.error('Failed to load roadmap:', error);
            setView('create');
        }
    };

    // [New] Animation Cleanup Effect
    useEffect(() => {
        if (newlyUnlockedTopic) {
            console.log('✨ 애니메이션 재생: ', newlyUnlockedTopic);
            const timer = setTimeout(() => {
                setNewlyUnlockedTopic(null); // 애니메이션 후 상태 초기화
            }, 4000); // 4초 후 해제
            return () => clearTimeout(timer);
        }
    }, [newlyUnlockedTopic, setNewlyUnlockedTopic]);

    const updateProgress = (data) => {
        setProgress(data.total_progress || 0);
    };

    const handleGenerate = async () => {
        if (!goal.trim()) return;

        setIsGenerating(true);
        setView('loading');

        try {
            const res = await api.generateRoadmap(goal);
            if (res && res.phases) {
                setRoadmap(res);
                updateProgress(res);
                setView('roadmap');
            } else {
                throw new Error('Empty roadmap result');
            }
        } catch (error) {
            console.error('Generation failed:', error);
            alert('로드맵 생성에 실패했습니다: ' + error.message);
            setView('create');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('정말 로드맵을 초기화하시겠습니까?\n모든 진행 상황이 삭제됩니다.')) return;

        try {
            await api.resetRoadmap();

            // [Fix] 프론트엔드 상태도 함께 초기화 (채팅, 레슨, 사이드바 등)
            resetAll();

            setRoadmap(null);
            setGoal('');
            setView('create');
        } catch (error) {
            console.error('Reset failed:', error);
            alert('초기화 실패');
        }
    };

    const handleStartLesson = (topic, pIdx, tIdx) => {
        // [Safety Check] 사이드 퀘스트 진행 중인지 확인
        const suspendedCtx = useLunaStore.getState().suspendedRoadmapContext;
        if (suspendedCtx) {
            if (!window.confirm('⚠️ 현재 "단기 커리큘럼(Side Quest)"이 진행 중입니다.\n\n로드맵 레슨을 시작하면 현재 진행 중인 내용은 로드맵 모드로 전환됩니다.\n계속하시겠습니까?')) {
                return;
            }
        }

        // [Fix] 기존 레슨/커리큘럼 상태 초기화 (이전 토픽 잔류 방지)
        const store = useLunaStore.getState();
        store.setCurriculum(null);
        store.setLessonPlan(null);
        store.setStepIndex(0);
        store.setLessonStepIndex(0);
        store.setLessonCompleted(false);
        store.setProblem(null);
        store.setChatHistory([]);
        store.setLastCodeSnapshot(null);

        // [Fix] intendedTopic 설정 (커리큘럼 생성 시 사용)
        store.setIntendedTopic(topic.title);

        // [Fix] appState를 IDLE로 전환 (이전 상태 잔류 방지)
        store.setAppState('IDLE');

        setRoadmapMode(true, {
            topic: topic.title,
            description: topic.description,
            pIdx,
            tIdx,
            topicId: topic.id || null, // [New] 토픽 ID 전달 (인덱스 보정용)
            phaseTitle: roadmap?.phases[pIdx]?.title || `Phase ${pIdx + 1}`,
            roadmapTitle: roadmap?.roadmap_title || roadmap?.title || '나만의 커스텀 로드맵'
        });

        // [Fix] needsCurriculumGeneration 플래그 설정 (메인 페이지에서 자동 생성 트리거)
        store.setNeedsCurriculumGeneration(true);

        // [Fix] 상태 즉시 저장 (새로고침 시 인덱스 회귀 방지)
        saveCurrentState();

        console.log('[RoadmapPage] 토픽 전환:', topic.title, '→ 커리큘럼 자동 생성 예약');
        navigate('/');
    };

    // Render Helpers
    const renderCreateView = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center min-h-[80vh] px-4"
        >
            <div className="max-w-lg w-full text-center relative z-10">
                {/* Background Glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none"></div>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="w-24 h-24 mx-auto mb-8 relative group"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-3xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-500 animate-pulse-slow"></div>
                    <div className="relative w-full h-full glass-panel rounded-3xl flex items-center justify-center overflow-hidden">
                        <span className="text-5xl relative z-10">🗺️</span>
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50"></div>
                    </div>
                </motion.div>

                <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-slate-400 mb-4 tracking-tight">
                    Design Your Career Path
                </h2>
                <p className="text-slate-400 mb-10 leading-relaxed max-w-sm mx-auto">
                    원하는 커리어 목표를 입력하세요.<br />
                    <span className="text-cyan-400">AI Architect</span>가 당신만을 위한 마스터 플랜을 설계합니다.
                </p>

                <div className="relative mb-6 group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl opacity-30 group-hover:opacity-100 transition duration-300 blur"></div>
                    <input
                        type="text"
                        className="relative w-full bg-[#0f1115] border border-slate-700/50 rounded-xl px-6 py-4 text-white text-base placeholder-slate-600 focus:outline-none focus:text-cyan-200 transition-all shadow-inner"
                        placeholder="예: AI 엔지니어, 풀스택 개발자..."
                        value={goal}
                        onChange={(e) => setGoal(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                        disabled={isGenerating}
                    />
                </div>

                <div className="flex justify-center">
                    <button
                        onClick={handleGenerate}
                        disabled={isGenerating || !goal.trim()}
                        className="group relative w-full sm:w-auto min-w-[200px] py-4 px-8 rounded-xl font-bold text-white overflow-hidden transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 group-hover:from-cyan-500 group-hover:to-blue-500 transition-all duration-300"></div>
                        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-transparent via-cyan-300 to-transparent opacity-50 group-hover:opacity-100 blur-[2px]"></div>
                        <span className="relative flex items-center justify-center gap-2">
                            {isGenerating ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    <span>설계 중...</span>
                                </>
                            ) : (
                                <>
                                    <span>Generate Roadmap</span>
                                    <i className="fas fa-magic"></i>
                                </>
                            )}
                        </span>
                    </button>
                </div>

                <div className="mt-12">
                    <p className="text-[10px] text-slate-600 uppercase tracking-[0.2em] mb-4 font-semibold">Recommended Goals</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {['Python Backend', 'React Frontend', 'Data Scientist', 'iOS Developer', 'DevOps'].map((tag) => (
                            <button
                                key={tag}
                                onClick={() => setGoal(tag)}
                                className="px-4 py-2 bg-slate-900/50 hover:bg-slate-800 rounded-lg text-xs text-slate-400 hover:text-cyan-400 border border-slate-800 hover:border-cyan-500/30 transition-all duration-300"
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );

    const renderRoadmapView = () => (
        <div className="max-w-4xl mx-auto px-4 py-10 pb-32">
            {/* Hero Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-16 relative"
            >
                <div className="absolute top-0 right-0 p-4 opacity-20 pointer-events-none">
                    <i className="fas fa-map-marked-alt text-[8rem] text-slate-800"></i>
                </div>

                <span className="inline-block px-3 py-1 mb-4 text-[10px] font-bold bg-cyan-950/30 text-cyan-400 rounded-full border border-cyan-500/20 backdrop-blur-sm">
                    MASTER PLAN
                </span>
                <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-slate-400 mb-4 tracking-tight">
                    {roadmap?.roadmap_title || 'Career Roadmap'}
                </h2>
                <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                    <p className="text-base text-slate-400 leading-relaxed max-w-xl">
                        {roadmap?.description}
                    </p>

                    {/* Progress Stats */}
                    <div className="flex-1 w-full md:w-auto glass-panel p-5 rounded-2xl relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="flex justify-between items-end mb-2">
                            <div className="flex flex-col">
                                <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Total Progress</span>
                                <span className="text-3xl font-bold text-white">{progress}<span className="text-lg text-slate-600">%</span></span>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] text-slate-400 mb-1">Estimated Time</div>
                                <span className="px-2 py-1 bg-slate-800/50 rounded text-xs text-cyan-300 font-mono">
                                    {roadmap?.estimated_duration || '3-6 Months'}
                                </span>
                            </div>
                        </div>
                        {/* Progress Bar */}
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 1.5, ease: "easeOut" }}
                                className="h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 relative"
                            >
                                <div className="absolute inset-0 bg-white/30 animate-[shimmer_2s_infinite]"></div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Timeline Container */}
            <div className="relative">
                {/* Central Beam Line */}
                <div className="absolute left-[27px] top-6 bottom-0 w-[2px] bg-slate-800/50">
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${progress}%` }}
                        transition={{ duration: 2, ease: "easeInOut" }}
                        className="w-full bg-gradient-to-b from-cyan-500 via-blue-500 to-purple-600 shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                    />
                </div>

                <div className="space-y-12">
                    {roadmap?.phases?.map((phase, pIdx) => {
                        const topics = phase.topics || [];
                        const completedTopics = topics.filter(t => t.status === 'completed').length;
                        const isPhaseComplete = completedTopics === topics.length && topics.length > 0;
                        const isPhaseActive = completedTopics > 0 || pIdx === 0 || (roadmap.phases[pIdx - 1]?.topics.every(t => t.status === 'completed'));

                        return (
                            <motion.div
                                key={pIdx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: pIdx * 0.1 }}
                                className="relative grid grid-cols-[56px_1fr] gap-8"
                            >
                                {/* Spaced Grid Layout for Icon and Content */}

                                {/* Icon Column */}
                                <div className="relative flex flex-col items-center">
                                    <div className={`
                                        relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-xl transition-all duration-500
                                        ${isPhaseComplete
                                            ? 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-[0_0_20px_rgba(16,185,129,0.3)] scale-105'
                                            : isPhaseActive
                                                ? `bg-[#0f1115] border-2 border-${phase.color || 'cyan'}-500 text-${phase.color || 'cyan'}-400 shadow-[0_0_20px_rgba(6,182,212,0.2)]`
                                                : 'bg-[#0f1115] border border-slate-700 text-slate-600 grayscale'}
                                    `}>
                                        {isPhaseComplete ? (
                                            <i className="fas fa-check text-white"></i>
                                        ) : (
                                            phase.icon ? (
                                                <SafeFaIcon
                                                    icon={phase.icon}
                                                    className={isPhaseActive ? '' : 'opacity-50'}
                                                />
                                            ) : (
                                                <span className="font-black">{pIdx + 1}</span>
                                            )
                                        )}

                                        {/* Active Halo */}
                                        {isPhaseActive && !isPhaseComplete && (
                                            <div className="absolute inset-0 rounded-2xl border border-cyan-400/50 animate-ping"></div>
                                        )}
                                    </div>
                                </div>

                                {/* Content Column */}
                                <div>
                                    <div className="mb-6">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${isPhaseActive ? 'bg-cyan-950/30 text-cyan-400 border-cyan-500/20' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                                                Phase {pIdx + 1}
                                            </span>
                                            {isPhaseComplete && <span className="text-[10px] text-emerald-400 font-bold">COMPLETED</span>}
                                        </div>
                                        <h3 className={`text-xl font-bold ${isPhaseActive ? 'text-white' : 'text-slate-500'} transition-colors`}>
                                            {phase.title?.replace(/^Phase\s*\d+\s*[:\-]\s*/i, '')}
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-1 max-w-xl leading-relaxed">
                                            {phase.description}
                                        </p>
                                    </div>

                                    {/* Topic Cards Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {topics.map((topic, tIdx) => {
                                            const status = topic.status || 'locked';
                                            const isActive = status === 'active';
                                            const isCompleted = status === 'completed';

                                            // [New] 방금 언락된 노드인지 확인
                                            const isJustUnlocked = newlyUnlockedTopic &&
                                                newlyUnlockedTopic.phase_index === pIdx &&
                                                newlyUnlockedTopic.topic_index === tIdx;

                                            return (
                                                <motion.div
                                                    key={tIdx}
                                                    onClick={() => isActive && handleStartLesson(topic, pIdx, tIdx)}
                                                    className={`
                                                        group relative p-4 rounded-xl border transition-all duration-500 overflow-hidden
                                                        ${isActive
                                                            ? 'bg-[#0f1115] border-cyan-500/30 cursor-pointer hover:-translate-y-1 hover:shadow-[0_5px_20px_rgba(6,182,212,0.1)] hover:border-cyan-400/50'
                                                            : isCompleted
                                                                ? 'bg-emerald-900/10 border-emerald-500/20 opacity-80'
                                                                : 'bg-[#0f1115] border-slate-800 cursor-not-allowed opacity-50'}
                                                    `}
                                                    animate={isJustUnlocked ? {
                                                        x: [-2, 2, -2, 2, 0],
                                                        transition: { duration: 0.4, ease: "easeInOut" }
                                                    } : {}}
                                                >
                                                    {/* [New] Elegant Unlock Effect: Light Sweep + Border Glow */}
                                                    <AnimatePresence>
                                                        {isJustUnlocked && (
                                                            <>
                                                                {/* 1. Warm Yellow Glow Pulse */}
                                                                <motion.div
                                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                                    animate={{ opacity: [0, 1, 0], scale: [0.8, 1.1, 1] }}
                                                                    transition={{ duration: 1.5, ease: "easeOut" }}
                                                                    className="absolute inset-0 bg-amber-400/20 rounded-xl pointer-events-none"
                                                                />
                                                            </>
                                                        )}
                                                    </AnimatePresence>

                                                    {/* Active Hover Glow (Existing) */}
                                                    {isActive && <div className="absolute inset-0 bg-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>}

                                                    <div className="flex items-start gap-4 relaltive z-10">
                                                        <div className={`mt-1 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold transition-all duration-700
                                                            ${isActive ? 'bg-cyan-500/10 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-white' :
                                                                isCompleted ? 'bg-emerald-500/10 text-emerald-500' :
                                                                    'bg-slate-800 text-slate-600'}
                                                        `}>
                                                            {isJustUnlocked ? (
                                                                <motion.div
                                                                    initial={{ rotate: 0, scale: 1 }}
                                                                    animate={{
                                                                        rotate: [0, -15, 15, -15, 15, 0], // Clicky Shake
                                                                        scale: [1, 1.3, 1], // Pop
                                                                    }}
                                                                    transition={{ duration: 0.5, ease: "backOut" }}
                                                                >
                                                                    <i className="fas fa-unlock text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]"></i>
                                                                </motion.div>
                                                            ) : (
                                                                isCompleted ? <i className="fas fa-check"></i> : isActive ? <i className="fas fa-play"></i> : <i className="fas fa-lock"></i>
                                                            )}
                                                        </div>

                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <h4 className={`text-sm font-bold truncate ${isActive ? 'text-slate-200 group-hover:text-cyan-300' : 'text-slate-500'}`}>
                                                                    {topic.title}
                                                                </h4>
                                                            </div>
                                                            <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                                                                {topic.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    // Custom Header Action for Reset
    const resetButton = view === 'roadmap' ? (
        <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-all duration-300 shadow-[0_0_15px_rgba(239,68,68,0.3)] hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] active:scale-95 border border-transparent"
        >
            <i className="fas fa-trash-alt group-hover:rotate-12 transition-transform"></i>
            Reset Roadmap
        </button>
    ) : null;

    return (
        <div className="relative w-full h-screen bg-[#0B0E14] overflow-hidden text-white flex flex-col selection:bg-cyan-500/30 selection:text-cyan-100">
            {/* Background - Grid + Aurora Blobs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="aurora-blob blob-1"></div>
                <div className="aurora-blob blob-2"></div>
                <div className="aurora-blob blob-3"></div>
                <div className="bg-grid-pattern"></div>
            </div>

            {/* Shared Header with Custom Reset Action */}
            <Header
                onNewTopic={() => setView('create')}
                toggleSound={() => setIsSoundOn(!isSoundOn)}
                isSoundOn={isSoundOn}
                username={username}
                customAction={resetButton}
                onShowTerms={() => setShowTerms(true)}
            />

            {/* Main Content */}
            <main className="relative z-10 flex-1 overflow-y-auto custom-scrollbar">
                {view === 'loading' && (
                    <div className="h-full flex flex-col items-center justify-center">
                        <div className="relative w-16 h-16 mb-6">
                            <div className="absolute inset-0 border-4 border-slate-800 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-t-cyan-500 rounded-full animate-spin"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xl">🚀</div>
                        </div>
                        <p className="text-sm text-slate-400 font-medium animate-pulse">Designing your future...</p>
                    </div>
                )}
                {view === 'create' && renderCreateView()}
                {view === 'roadmap' && renderRoadmapView()}
            </main>

            {/* Terms Modal - Mounted outside main flow */}
            <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
        </div>
    );
}
